import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import iconExcel from "../../assets/images/employee/iconExcel.svg";
import plus from "../../assets/images/product/plus.svg";
import TableCustomers from "./tableCustomers";
import FormNew from "./formCustomers/formNew";
import { useCustomerList } from "../../hooks/useCustomer";
import toast from "react-hot-toast";
import useDebounce from "../../hooks/useDebounce";
import { customerCall } from "../../api/customer";
import { exportFile } from "../../utils/tools";
import FormNewBusiness from "./formCustomers/formNewBusiness";
import PopupImport from "../../components/popup/PopupImport";
import IconFile from "../../assets/images/booking/upfile.svg";

const TABLE_HEADERS = [
  { label: "", key: "" },
  { label: "STT", key: "" },
  { label: "Tên", key: "name" },
  { label: "Giới tính", key: "gender" },
  { label: "Loại khách hàng", key: "isBusiness" },
  { label: "Phân loại", key: "type" },
  { label: "Dự án quan tâm", key: "project" },
  { label: "Số điện thoại", key: "phone" },
  { label: "Email", key: "email" },
  { label: "Mã số thuế", key: "businessTaxCode" },
  { label: "Loại giấy tờ tuỳ thân", key: "identityNumber" },
  { label: "CCCD/CMND hoặc Hộ chiếu", key: "identificationNumber" },
  { label: "Nhân viên tạo", key: "employee" },
  { label: "Khối", key: "saleBlock" },
  { label: "Vùng", key: "saleArea" },
  { label: "Thông tin khách hàng cá nhân", key: "identificationInfo" },
  { label: "Ảnh CCCD mặt trước", key: "idCardFrontImage" },
  { label: "Ảnh CCCD mặt sau", key: "idCardBackImage" },
  { label: "Ảnh VNID", key: "vnidImage" },
  { label: "Thông tin khách hàng doanh nghiệp", key: "identificationInfoBusiness" },
  { label: "Ảnh CCCD mặt trước của NĐD", key: "idRepresentativeCardFrontImage" },
  { label: "Ảnh CCCD mặt sau của NĐD", key: "idRepresentativeCardBackImage" },
  { label: "Ảnh VNID của NĐD", key: "representativeVnidImage" },
  { label: "Ảnh ĐKKD danh nghiệp", key: "businessRegistrationImage" },
  { label: "Số tài khoản", key: "bankAccount" },
  { label: "Tên ngân hàng", key: "bankName" },
  { label: "Chủ tài khoản ngân hàng", key: "nameAccount" },
];

// Cột file Excel khách hàng theo file mẫu của khách, dùng chung cho xuất excel,
// import và file mẫu. Khách cá nhân và doanh nghiệp gộp chung cột:
// CCCD/MST, ngày cấp, nơi cấp, hộ khẩu/địa chỉ ĐKKD, địa chỉ liên hệ/VP giao dịch.
// Phải khớp COLUMN_LABELS ở backend-v1/src/controllers/customer/import.js
const CUSTOMER_EXCEL_COLUMNS = [
  { key: "name", label: "Tên" },
  { key: "genderLabel", label: "Giới tính" },
  { key: "identificationNumber", label: "CCCD/CMND/Hộ chiếu MST" },
  { key: "dateOfIdentification", label: "Ngày cấp" },
  { key: "placeOfIdentification", label: "Nơi cấp" },
  { key: "dateOfBirth", label: "Ngày sinh" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Số điện thoại" },
  { key: "permanentAddress", label: "Hộ khẩu thường trú\nĐịa chỉ đăng ký KD" },
  { key: "currentAddress", label: "Địa chỉ liên hệ\nVăn phòng giao dịch" },
  { key: "projectNames", label: "Dự án quan tâm" },
  { key: "identityTypeLabel", label: "Loại giấy tờ tuỳ thân" },
  { key: "customerTypeName", label: "Phân loại" },
  { key: "jobName", label: "Nghề nghiệp" },
  { key: "customerKind", label: "Loại khách hàng" },
  { key: "representativePosition", label: "Chức vụ người đại diện" },
  { key: "representativeName", label: "Tên người đại diện" },
  { key: "representativeGenderLabel", label: "Giới tính người đại diện" },
  { key: "representativeIdNumber", label: "CCCD của người đại diện" },
  {
    key: "representativeIdIssuedDate",
    label: "Ngày cấp CCCD của người đại diện",
  },
  {
    key: "representativeIdIssuedPlace",
    label: "Nơi cấp CCCD của người đại diện",
  },
  { key: "representativeDateOfBirth", label: "Ngày sinh người đại diện" },
  { key: "representativeEmail", label: "Email người đại diện" },
  { key: "representativePhone", label: "Số điện thoại người đại diện" },
  { key: "bankAccount", label: "Số tài khoản" },
  { key: "bankName", label: "Tên ngân hàng " },
  { key: "nameAccount", label: "Chủ tài khoản ngân hàng" },
  { key: "employeeCode", label: "Mã nhân viên tạo" },
  { key: "employee", label: "Nhân viên tạo" },
  { key: "saleBlock", label: "Khối" },
  { key: "saleArea", label: "Vùng" },
  { key: "dateCreate", label: "Ngày tạo" },
];

// key -> tiêu đề cột (xuất excel)
const customerColumnMapping = Object.fromEntries(
  CUSTOMER_EXCEL_COLUMNS.map(({ key, label }) => [key, label]),
);

// tiêu đề cột -> key (import). importFile đã trim tiêu đề nên bỏ khoảng trắng
// thừa ở cuối, vd "Tên ngân hàng ". Tiêu đề xuống dòng trong file Excel của
// khách lưu là \r\n nên nhận cả 2 kiểu.
const customerColumnMappingReverse = Object.fromEntries(
  CUSTOMER_EXCEL_COLUMNS.flatMap(({ key, label }) => [
    [label.trim(), key],
    [label.trim().replace(/\n/g, "\r\n"), key],
  ]),
);

export default function Customers() {
  const {
    isOpen: isOpenPopupForm,
    onOpen: onOpenPopupForm,
    onClose: onClosePopupForm,
  } = useDisclosure();

  const {
    isOpen: isOpenCustomer,
    onOpen: onOpenCustomer,
    onClose: onCloseCustomer,
  } = useDisclosure();

  const {
    isOpen: isOpenCustomerBusiness,
    onOpen: onOpenCustomerBusiness,
    onClose: onCloseCustomerBusiness,
  } = useDisclosure();

  const [popupImportVisible, setPopupImportVisible] = useState(false);

  const [search, setSearch] = useState("");
  const [gender, setGender] = useState(null);
  const [type, setType] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [projectId, setProjectId] = useState([]);
  const [customerTypeId, setCustomerTypeId] = useState(null);
  const [saleBlockId, setSaleBlockId] = useState(null);
  const [saleAreaId, setSaleAreaId] = useState(null);

  const debouncedSearch = useDebounce(search, 500);

  const {
    optionsData,
    customerListData,
    refetch,
    totalPages,
    setCurrentPage,
    isLoading,
    isRefetching,
    currentPage,
    entries,
    startEntry,
    endEntry,
  } = useCustomerList(
    debouncedSearch,
    projectId,
    employeeId,
    customerTypeId,
    saleBlockId,
    saleAreaId,
    gender,
    type,
  );

  const handlePageChange = useCallback(
    (selectedPage) => {
      setCurrentPage(selectedPage.selected + 1);
    },
    [setCurrentPage],
  );

  const tableData = useMemo(
    () => ({
      headers: TABLE_HEADERS,
      data: customerListData,
    }),
    [customerListData],
  );

  const onSubmit = useCallback(
    async (data) => {
      const toastCreate = toast.loading("Đang thêm khách hàng...");

      try {
        const filteredData = Object.fromEntries(
          Object.entries(data).filter(
            ([, value]) => value !== "" && value !== undefined && value !== null,
          ),
        );

        if (filteredData?.customer_shares) {
          delete filteredData.customer_shares;
        }

        await customerCall.create({ ...filteredData });
        await refetch();
        onCloseCustomer();
        toast.success("Thêm thành công");
      } catch (error) {
        console.error("Error during customer creation:", error);
        toast.error("Có lỗi xảy ra khi thêm");
      } finally {
        toast.dismiss(toastCreate);
      }
    },
    [onCloseCustomer, refetch],
  );

  const onSubmitBusiness = useCallback(
    async (data) => {
      const toastCreate = toast.loading("Đang thêm khách hàng...");

      try {
        const filteredData = Object.fromEntries(
          Object.entries(data).filter(
            ([, value]) => value !== "" && value !== undefined && value !== null,
          ),
        );

        if (filteredData?.customer_shares) {
          delete filteredData.customer_shares;
        }

        await customerCall.createBusiness({ ...filteredData });
        await refetch();
        onCloseCustomerBusiness();
        toast.success("Thêm thành công");
      } catch (error) {
        console.error("Error during business customer creation:", error);
        toast.error("Có lỗi xảy ra khi thêm");
      } finally {
        toast.dismiss(toastCreate);
      }
    },
    [onCloseCustomerBusiness, refetch],
  );

  const handleExport = useCallback(async () => {
    if (!customerListData?.length) {
      toast.error("Không có dữ liệu");
      return;
    }

    const data = await customerCall.export(optionsData);
    if (data) {
      exportFile(data, customerColumnMapping, "danh_sach_khach_hang.xlsx");
      toast.success("Download file thành công");
    } else {
      toast.error("Không có dữ liệu");
    }
  }, [customerListData, optionsData]);

  const handleExportSample = () => {
    const data = [
      {
        name: "Nguyễn Văn A",
        genderLabel: "Nam",
        identificationNumber: "001090000001",
        dateOfIdentification: "12/07/2022",
        placeOfIdentification:
          "Cục Cảnh sát quản lý hành chính về trật tự xã hội",
        dateOfBirth: "05/05/1990",
        email: "nguyenvana@gmail.com",
        phone: "0900000001",
        permanentAddress: "Số 1 Đường A, Phường B, TP Hà Nội",
        currentAddress: "Số 1 Đường A, Phường B, TP Hà Nội",
        projectNames: "",
        identityTypeLabel: "CCCD/CMND",
        customerTypeName: "",
        jobName: "",
        customerKind: "Cá nhân",
        representativePosition: "",
        representativeName: "",
        representativeGenderLabel: "",
        representativeIdNumber: "",
        representativeIdIssuedDate: "",
        representativeIdIssuedPlace: "",
        representativeDateOfBirth: "",
        representativeEmail: "",
        representativePhone: "",
        bankAccount: "",
        bankName: "",
        nameAccount: "",
        employeeCode: "",
        employee: "",
        saleBlock: "",
        saleArea: "",
        dateCreate: "",
      },
      {
        name: "Công ty TNHH B",
        genderLabel: "",
        identificationNumber: "0100000001",
        dateOfIdentification: "01/01/2020",
        placeOfIdentification: "Sở Kế hoạch và Đầu tư TP Hà Nội",
        dateOfBirth: "",
        email: "congtyb@gmail.com",
        phone: "0900000002",
        permanentAddress: "Số 2 Đường C, Phường D, TP Hà Nội",
        currentAddress: "Số 2 Đường C, Phường D, TP Hà Nội",
        projectNames: "",
        identityTypeLabel: "CCCD/CMND",
        customerTypeName: "",
        jobName: "",
        customerKind: "Doanh nghiệp",
        representativePosition: "Giám đốc",
        representativeName: "Trần Văn C",
        representativeGenderLabel: "Nam",
        representativeIdNumber: "001080000002",
        representativeIdIssuedDate: "10/10/2021",
        representativeIdIssuedPlace:
          "Cục Cảnh sát quản lý hành chính về trật tự xã hội",
        representativeDateOfBirth: "20/10/1980",
        representativeEmail: "",
        representativePhone: "",
        bankAccount: "",
        bankName: "",
        nameAccount: "",
        employeeCode: "",
        employee: "",
        saleBlock: "",
        saleArea: "",
        dateCreate: "",
      },
    ];

    exportFile(data, customerColumnMapping, "mau_import_khach_hang.xlsx");
  };

  return (
    <Box
      w={{ base: "100%" }}
      backgroundColor="var(--color-backgroundmain)"
      p={{ base: "24px 16px 16px" }}
    >
      <Flex alignItems={"center"} justifyContent={"space-between"}>
        <Flex alignItems={"center"} gap={{ base: "74px" }}>
          <Text fontSize={{ base: "20px" }} fontWeight={600}>
            Quản lý khách hàng
          </Text>
        </Flex>

        <Flex alignItems={"center"} gap={"16px"}>
          <Button
            display={"flex"}
            alignItems={"center"}
            p={"10px 16px 10px 12px"}
            gap={"8px"}
            h={"44px"}
            backgroundImage="var(--color-button)"
            _hover={{ filter: "brightness(90%)" }}
            onClick={() => setPopupImportVisible(true)}
          >
            <Image src={IconFile} />
            <Text
              fontSize={{ base: "15px" }}
              fontWeight={600}
              color="var(--color-main)"
            >
              Import khách hàng
            </Text>
          </Button>

          <Button
            display={"flex"}
            alignItems={"center"}
            p={"10px 16px 10px 12px"}
            gap={"8px"}
            h={"44px"}
            backgroundImage="var(--linear-gradient-excel)"
            _hover={{ filter: "brightness(90%)" }}
            onClick={handleExport}
          >
            <Image src={iconExcel} />
            <Text
              fontSize={{ base: "15px" }}
              fontWeight={600}
              color="var(--color-main)"
            >
              Xuất excel
            </Text>
          </Button>

          <Button
            w={"176px"}
            h={"44px"}
            display={"flex"}
            alignItems={"center"}
            p={"10px 16px 10px 12px"}
            gap={"8px"}
            backgroundImage="var(--color-button)"
            _hover={{ filter: "brightness(90%)" }}
            onClick={onOpenPopupForm}
          >
            <Image src={plus} />
            <Text
              fontSize={{ base: "15px" }}
              fontWeight={600}
              color="var(--color-main)"
            >
              Tạo khách hàng
            </Text>
          </Button>
        </Flex>
      </Flex>

      <Box mt={{ base: "23px" }}>
        <TableCustomers
          tableData={tableData}
          search={search}
          setSearch={setSearch}
          employeeId={employeeId}
          setEmployeeId={setEmployeeId}
          projectId={projectId}
          setProjectId={setProjectId}
          totalPages={totalPages}
          isLoading={isLoading}
          isRefetching={isRefetching}
          currentPage={currentPage}
          entries={entries}
          startEntry={startEntry}
          endEntry={endEntry}
          handlePageChange={handlePageChange}
          refetch={refetch}
          setGender={setGender}
          setType={setType}
          customerTypeId={customerTypeId}
          setCustomerTypeId={setCustomerTypeId}
          saleBlockId={saleBlockId}
          setSaleBlockId={setSaleBlockId}
          saleAreaId={saleAreaId}
          setSaleAreaId={setSaleAreaId}
        />
      </Box>

      <FormNew
        isOpen={isOpenCustomer}
        onClose={onCloseCustomer}
        isEdit={false}
        onSubmit={onSubmit}
      />

      <FormNewBusiness
        isOpen={isOpenCustomerBusiness}
        onClose={onCloseCustomerBusiness}
        onSubmit={onSubmitBusiness}
        isEdit={false}
      />

      {popupImportVisible && (
        <PopupImport
          isOpen={popupImportVisible}
          onClose={() => setPopupImportVisible(false)}
          refetchCheckInData={refetch}
          columnMappingReverse={customerColumnMappingReverse}
          handleExport={handleExportSample}
          titleAdd="Import khách hàng"
          inputAdd="Nhập danh sách khách hàng"
          type={"customerImport"}
        />
      )}

      <Modal isOpen={isOpenPopupForm} onClose={onClosePopupForm}>
        <ModalOverlay />
        <ModalContent
          backgroundColor="var(--color-main)"
          borderRadius={"16px"}
          w={"100%"}
          maxW={"525px"}
          overflowY={"auto"}
          p={"24px"}
          sx={{
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              width: "4px",
              borderRadius: "50px",
              background: "var(--color-background)",
            },
            "&::-moz-scrollbar": { width: "4px" },
            "&::-moz-scrollbar-thumb": {
              width: "4px",
              borderRadius: "50px",
              background: "var(--color-background)",
            },
          }}
        >
          <Flex
            alignItems={"center"}
            gap={{ base: "74px" }}
            textAlign={"center"}
            justifyContent={"center"}
          >
            <Text
              fontSize={{ base: "20px" }}
              fontWeight={600}
              textAlign={"center"}
            >
              Lựa chọn loại khách hàng
            </Text>
          </Flex>

          <Flex
            alignItems={"center"}
            gap={"16px"}
            justifyContent={"space-between"}
            pt={"32px"}
          >
            <Button
              display="flex"
              alignItems="center"
              p={"10px 16px 10px 12px"}
              gap="8px"
              backgroundImage="var(--color-button)"
              onClick={() => {
                onClosePopupForm();
                onOpenCustomer();
              }}
              _hover={{ filter: "brightness(90%)" }}
              color="var(--color-main)"
              w={"176px"}
              h={"44px"}
            >
              Cá nhân
            </Button>

            <Button
              w={"176px"}
              h={"44px"}
              display={"flex"}
              alignItems={"center"}
              p={"10px 16px 10px 12px"}
              gap={"8px"}
              background="var(--linear-gradient-employee)"
              _hover={{ filter: "brightness(90%)" }}
              color="var(--color-main)"
              onClick={() => {
                onClosePopupForm();
                onOpenCustomerBusiness();
              }}
            >
              Doanh nghiệp
            </Button>
          </Flex>
        </ModalContent>
      </Modal>
    </Box>
  );
}